import { createContext, useContext, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { useGetWellbeingSettings, getGetWellbeingSettingsQueryKey } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";
import { playChime } from "@/lib/chime";
import { useNotificationPrefs } from "./NotificationPrefs";

const STORAGE_KEY = "assistio.pomodoro.state";

export type PomodoroPhase = "idle" | "work" | "break" | "longBreak";

type PersistedState = {
  phase: PomodoroPhase;
  // Absolute timestamp (ms) the current phase ends at, while running. Deriving
  // remaining time from this (rather than accumulating a countdown) means a
  // page reload or a stalled tab doesn't drift — it's always "what time does
  // this end", recomputed against the real clock.
  phaseEndsAt: number | null;
  // Set instead of phaseEndsAt while paused, so resuming can pick up exactly
  // where it left off.
  pausedRemainingMs: number | null;
  sessionsCompleted: number;
};

const IDLE_STATE: PersistedState = { phase: "idle", phaseEndsAt: null, pausedRemainingMs: null, sessionsCompleted: 0 };

function loadInitialState(): PersistedState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return IDLE_STATE;
    const parsed = JSON.parse(saved) as PersistedState;
    // The tab was closed/asleep past when this phase should have ended —
    // don't try to silently fast-forward through however many cycles were
    // missed, just land back on idle.
    if (parsed.phaseEndsAt && parsed.phaseEndsAt <= Date.now()) {
      return { ...IDLE_STATE, sessionsCompleted: parsed.sessionsCompleted };
    }
    return parsed;
  } catch {
    return IDLE_STATE;
  }
}

function saveState(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable — pomodoro state just won't survive a reload
  }
}

type PomodoroContextValue = {
  phase: PomodoroPhase;
  isPaused: boolean;
  remainingMs: number;
  sessionsCompleted: number;
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
};

const PomodoroContext = createContext<PomodoroContextValue | null>(null);

export function PomodoroProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useGetWellbeingSettings({ query: { queryKey: getGetWellbeingSettingsQueryKey() } });
  const { soundEnabled, notificationPermission } = useNotificationPrefs();
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  const notificationPermissionRef = useRef(notificationPermission);
  useEffect(() => { notificationPermissionRef.current = notificationPermission; }, [notificationPermission]);

  const durationsRef = useRef({ work: 25, break: 5, longBreak: 15, sessionsBeforeLongBreak: 4 });
  useEffect(() => {
    if (!settings) return;
    durationsRef.current = {
      work: settings.pomodoroWorkMinutes,
      break: settings.pomodoroBreakMinutes,
      longBreak: settings.pomodoroLongBreakMinutes,
      sessionsBeforeLongBreak: settings.pomodoroSessionsBeforeLongBreak,
    };
  }, [settings]);

  const [state, setState] = useState<PersistedState>(loadInitialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [, forceTick] = useReducer((c: number) => c + 1, 0);

  const update = (next: PersistedState) => {
    setState(next);
    saveState(next);
  };

  const phaseLabel = (phase: PomodoroPhase) => (phase === "work" ? "Work session" : phase === "longBreak" ? "Long break" : "Break");

  const advancePhase = () => {
    const current = stateRef.current;
    const d = durationsRef.current;
    let next: PersistedState;
    if (current.phase === "work") {
      const sessionsCompleted = current.sessionsCompleted + 1;
      const isLongBreak = sessionsCompleted % d.sessionsBeforeLongBreak === 0;
      const minutes = isLongBreak ? d.longBreak : d.break;
      next = { phase: isLongBreak ? "longBreak" : "break", phaseEndsAt: Date.now() + minutes * 60_000, pausedRemainingMs: null, sessionsCompleted };
    } else {
      next = { phase: "work", phaseEndsAt: Date.now() + d.work * 60_000, pausedRemainingMs: null, sessionsCompleted: current.sessionsCompleted };
    }
    update(next);
    if (soundEnabledRef.current) playChime();

    const description = next.phase === "work" ? "Back to it." : "Time to step away for a bit.";
    toast({ title: phaseLabel(next.phase), description });

    if (notificationPermissionRef.current === "granted") {
      // Square appetised {a} icon (icon-192.png) — the OS notification icon
      // slot is square, so this uses the proper square export.
      const notification = new Notification(phaseLabel(next.phase), { body: description, icon: "/icon-192.png" });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  useEffect(() => {
    if (!state.phaseEndsAt) return;
    const interval = setInterval(() => {
      if (Date.now() >= stateRef.current.phaseEndsAt!) advancePhase();
      else forceTick();
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phaseEndsAt]);

  const start = () => {
    const d = durationsRef.current;
    update({ phase: "work", phaseEndsAt: Date.now() + d.work * 60_000, pausedRemainingMs: null, sessionsCompleted: 0 });
  };

  const pause = () => {
    if (!state.phaseEndsAt) return;
    update({ ...state, phaseEndsAt: null, pausedRemainingMs: Math.max(0, state.phaseEndsAt - Date.now()) });
  };

  const resume = () => {
    if (state.pausedRemainingMs === null) return;
    update({ ...state, phaseEndsAt: Date.now() + state.pausedRemainingMs, pausedRemainingMs: null });
  };

  const reset = () => update(IDLE_STATE);

  const remainingMs = state.phaseEndsAt
    ? Math.max(0, state.phaseEndsAt - Date.now())
    : (state.pausedRemainingMs ?? 0);

  return (
    <PomodoroContext.Provider
      value={{
        phase: state.phase,
        isPaused: state.phase !== "idle" && state.phaseEndsAt === null,
        remainingMs,
        sessionsCompleted: state.sessionsCompleted,
        start, pause, resume, reset,
      }}
    >
      {children}
    </PomodoroContext.Provider>
  );
}

export function usePomodoro(): PomodoroContextValue {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error("usePomodoro must be used within PomodoroProvider");
  return ctx;
}
